import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, generatePath } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
	Building2,
	Compass,
	Loader2,
	MapPin,
	Navigation,
	AlertTriangle,
	Search,
	X,
} from 'lucide-react'
import { Button } from 'components/ui/button'
import MapLocationPopupCN from 'components/popups/MapLocationPopupCN'
import { fetchAllSites } from 'slices/evaluationSiteSlice'
import { initializeLocations } from 'slices/locationSlice'
import { fieldRoutes } from 'constants/fieldRoutes'
import { cn } from 'lib/utils'
import FieldShell from '../components/FieldShell'
import FieldAvatar from '../components/FieldAvatar'
import useCurrentPosition from '../hooks/useCurrentPosition'
import {
	distanceMetres,
	formatDistance,
	hasCoords,
	sortByDistance,
	type Coords,
	type WithDistance,
} from '../utils/distance'
import { directionsUrl } from '../utils/directions'

/**
 * How far the user must move before the list is allowed to reorder.
 *
 * `watchPosition` fires about once a second and the fix jitters by tens of
 * metres while standing still, so re-sorting on every update makes rows swap
 * places under the user's thumb: they tap the wrong one. Fifty metres is far
 * enough that nothing reorders while parked and close enough that the list is
 * right again before the next stop.
 */
const RESORT_THRESHOLD_M = 50

type Kind = 'site' | 'location'

interface NearbyRecord {
	key: string
	id: string
	kind: Kind
	name: string
	addressLine: string
	/**
	 * The address in parts as well as joined. The map popup takes them
	 * separately, and sites and locations disagree on the state field's name,
	 * so the reconciliation belongs here rather than at the call site.
	 */
	address: string
	city: string
	stateProvince: string
	/** The record's own picture, shown in place of a generic icon. */
	imageUrl?: string
	latitude?: number | null
	longitude?: number | null
}

const addressLineOf = (parts: (string | undefined | null)[]): string =>
	parts.filter(Boolean).join(', ')

/**
 * Locations and sites, nearest first.
 *
 * **A list, not a map.** This screen carried a map strip until the field app
 * became the whole mobile app: at 208px it could show a handful of pins and
 * nothing else useful, it fought the list for every touch, and its lettered
 * markers needed matching letters on the rows to mean anything at all. A list
 * with each record's own photograph says more in the same space, and the
 * picture is what a rep actually recognises a centre by.
 *
 * Proximity still orders it, and search still reaches past proximity for the
 * airport case. What went is the picture of where things are, not the knowing.
 */
const NearMe = () => {
	const dispatch = useDispatch()
	const navigate = useNavigate()

	const { entities: siteEntities, allSitesInitialized } = useSelector(
		(state: any) => state.evaluationSites,
	)
	const {
		entities: locationEntities,
		ids: locationIds,
		isInitialized: locationsInitialized,
	} = useSelector((state: any) => state.locations)

	const { position, status, error: positionError, request } = useCurrentPosition({
		immediate: true,
		watch: true,
	})

	// The position the list is currently sorted against. Deliberately lags the
	// live fix: see RESORT_THRESHOLD_M.
	const [anchor, setAnchor] = useState<Coords | null>(null)
	const [query, setQuery] = useState('')
	/** The record whose address pill was tapped, or null when the map is shut. */
	const [mapRecord, setMapRecord] = useState<NearbyRecord | null>(null)

	useEffect(() => {
		if (!allSitesInitialized) (dispatch as any)(fetchAllSites())
	}, [dispatch, allSitesInitialized])

	useEffect(() => {
		if (!locationsInitialized) (dispatch as any)(initializeLocations())
	}, [dispatch, locationsInitialized])

	useEffect(() => {
		if (!position) return
		const next = { latitude: position.latitude, longitude: position.longitude }
		setAnchor(prev => (prev && distanceMetres(prev, next) < RESORT_THRESHOLD_M ? prev : next))
	}, [position])

	const records = useMemo<NearbyRecord[]>(() => {
		const sites: NearbyRecord[] = Object.values(siteEntities ?? {}).map((s: any) => ({
			key: `site:${s._id}`,
			id: s._id,
			kind: 'site',
			name: s.name || 'Untitled site',
			addressLine: addressLineOf([s.address, s.city, s.state]),
			address: s.address || '',
			city: s.city || '',
			// A site calls it `state`; a location calls it `stateProvince`.
			stateProvince: s.state || '',
			// coverImage is derived server-side from the most recent SiteImage;
			// imageUrls is whatever was typed into the site form.
			imageUrl: s.coverImage || s.imageUrls?.[0],
			latitude: s.latitude,
			longitude: s.longitude,
		}))

		const locations: NearbyRecord[] = (locationIds ?? []).map((id: string) => {
			const l = locationEntities[id] || {}
			return {
				key: `location:${id}`,
				id,
				kind: 'location' as Kind,
				name: l.assetName || 'Untitled location',
				addressLine: addressLineOf([l.address, l.city, l.stateProvince]),
				address: l.address || '',
				city: l.city || '',
				stateProvince: l.stateProvince || '',
				imageUrl: l.image,
				latitude: l.latitude,
				longitude: l.longitude,
			}
		})

		return [...sites, ...locations]
	}, [siteEntities, locationEntities, locationIds])

	/**
	 * Search, because proximity is the wrong access path when you are nowhere
	 * near the thing. Catching up on a location from an airport is the case this
	 * exists for. Filtering the list already loaded needs no request.
	 */
	const matching = useMemo(() => {
		const needle = query.trim().toLowerCase()
		if (!needle) return records
		return records.filter(
			record =>
				record.name.toLowerCase().includes(needle) ||
				record.addressLine.toLowerCase().includes(needle),
		)
	}, [records, query])

	const ranked = useMemo(() => sortByDistance(matching, anchor), [matching, anchor])

	const openRecord = useCallback(
		(record: NearbyRecord) => {
			navigate(
				record.kind === 'site'
					? generatePath(fieldRoutes.SITE, { siteId: record.id })
					: generatePath(fieldRoutes.LOCATION, { locationId: record.id }),
			)
		},
		[navigate],
	)

	const openDirections = useCallback((record: NearbyRecord) => {
		window.location.href = directionsUrl({
			latitude: record.latitude as number,
			longitude: record.longitude as number,
			label: record.name,
		})
	}, [])

	const locating = status === 'locating' && !anchor
	const unplaceable = ranked.filter(r => r.metres === null && anchor).length

	return (
		<FieldShell title="Locations">
			<div className="pb-6">
				<div className="sticky top-0 z-20 bg-gray-50 px-3 py-2">
					<div className="relative">
						<Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
						<input
							value={query}
							onChange={e => setQuery(e.target.value)}
							placeholder="Search your sites and locations"
							aria-label="Search your sites and locations"
							className="w-full rounded-full border border-gray-300 bg-white py-2 pl-9 pr-9 text-base"
							// 16px or larger, or iOS zooms the page on focus.
							style={{ fontSize: '16px' }}
							autoCapitalize="none"
							autoCorrect="off"
							spellCheck={false}
						/>
						{query && (
							<button
								type="button"
								onClick={() => setQuery('')}
								aria-label="Clear search"
								className="absolute right-1 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-gray-400"
							>
								<X className="h-4 w-4" />
							</button>
						)}
					</div>
				</div>

				{locating && (
					<p className="flex items-center gap-2 px-4 py-3 text-sm text-gray-500">
						<Loader2 className="h-4 w-4 animate-spin" />
						Finding your location…
					</p>
				)}

				{positionError && (
					<div className="m-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
						<p className="flex items-start gap-2 text-sm text-amber-800">
							<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
							{positionError}
						</p>
						{/* The records are still worth showing. An unsorted list you can
						    read beats an error page. */}
						<p className="mt-1 text-xs text-amber-700">
							Your sites and locations are listed below, in their usual order rather than
							by distance.
						</p>
						{status !== 'denied' && (
							<Button variant="ghost" isInput onClick={request} className="mt-2">
								Try again
							</Button>
						)}
					</div>
				)}

				{ranked.length === 0 ? (
					<div className="m-3 rounded-lg border border-dashed border-gray-300 bg-white p-6 text-center">
						{query ? (
							<>
								<p className="text-sm font-medium text-gray-800">
									Nothing matches &quot;{query}&quot;
								</p>
								<p className="mt-1 text-sm text-gray-500">
									Search looks at names and addresses.
								</p>
							</>
						) : (
							<>
								<p className="text-sm font-medium text-gray-800">Nothing here yet</p>
								<p className="mt-1 text-sm text-gray-500">
									Capture the first one with <span className="font-medium">New Site</span>.
								</p>
							</>
						)}
					</div>
				) : (
					<ul className="divide-y divide-gray-100 border-y border-gray-200 bg-white">
						{ranked.map(item => (
							<NearbyRow
								key={item.record.key}
								item={item}
								onDirections={openDirections}
								onOpen={openRecord}
								onShowMap={setMapRecord}
							/>
						))}
					</ul>
				)}

				{unplaceable > 0 && (
					<p className="px-4 pt-3 text-xs text-gray-400">
						{unplaceable} {unplaceable === 1 ? 'record has' : 'records have'} no coordinates,
						so {unplaceable === 1 ? 'it is' : 'they are'} listed last.
					</p>
				)}

			</div>

			{/*
				Mounted only while open, so the map is built on demand rather than
				once per row. It sizes itself for a phone already.
			*/}
			{mapRecord && (
				<MapLocationPopupCN
					open
					onClose={() => setMapRecord(null)}
					location={{
						latitude: mapRecord.latitude as number,
						longitude: mapRecord.longitude as number,
						assetName: mapRecord.name,
						address: mapRecord.address,
						city: mapRecord.city,
						stateProvince: mapRecord.stateProvince,
						image: mapRecord.imageUrl,
					}}
				/>
			)}
		</FieldShell>
	)
}

interface NearbyRowProps {
	item: WithDistance<NearbyRecord>
	onDirections: (record: NearbyRecord) => void
	onOpen: (record: NearbyRecord) => void
	onShowMap: (record: NearbyRecord) => void
}

const NearbyRow = ({ item, onDirections, onOpen, onShowMap }: NearbyRowProps) => {
	const { record, metres, bearing } = item
	const Fallback = record.kind === 'site' ? MapPin : Building2
	const placeable = hasCoords(record)

	return (
		<li className="flex items-center gap-3 px-4 py-3">
			{/*
				The record's own photograph, which is how a rep recognises a centre.
				A generic pin told you only what kind of row it was, which the name
				already said.
			*/}
			<FieldAvatar
				src={record.imageUrl}
				fallbackIcon={Fallback}
				label={record.kind === 'site' ? 'Site' : 'Location'}
				iconClassName={cn(record.kind === 'site' && 'text-primary')}
			/>

			{/*
				Two separate controls, not one. The name opens the record and the
				address opens the map, so nesting the pill inside the row button
				would be invalid markup and would fire both on one tap.
			*/}
			{/*
				`min-h-0` on both controls below is deliberate, and it is the whole
				reason this column is not 88px tall.

				styles/… applies `min-height: 2.75rem` to EVERY button under 1024px,
				which is right for a standalone control and wrong for a line of text
				that happens to be tappable: two stacked buttons reserved 88px to
				hold 31px of type, and the name and address drifted apart. These two
				are wide instead of tall, so the target stays reachable while the
				rows stay tight.
			*/}
			<div className="min-w-0 flex-1">
				<button
					type="button"
					onClick={() => onOpen(record)}
					className="block min-h-0 w-full truncate text-left text-sm font-medium leading-tight text-primary"
				>
					{record.name}
				</button>
				{/*
					Deliberately lighter than the desktop pill it is modelled on.
					Filled and bordered, at a phone's row width, the address stretches
					into a grey bar that outweighs the name above it. Here it is an
					outline only, and the fill arrives on press instead, so the pin
					still reads as "tap me for the map" without shouting over the row.
				*/}
				<button
					type="button"
					onClick={() => onShowMap(record)}
					disabled={!placeable}
					title={placeable ? undefined : 'No coordinates available'}
					className={cn(
						'-ml-1.5 mt-0.5 inline-flex min-h-0 max-w-full items-center gap-1 rounded-full border border-transparent px-1.5 py-0 text-[11px] leading-4 transition-colors',
						placeable
							? 'cursor-pointer text-gray-500 active:border-[#0080fe]/20 active:bg-[#0080fe]/10 active:text-[#0080fe]'
							: 'cursor-not-allowed text-gray-400',
					)}
				>
					<MapPin className="h-2.5 w-2.5 shrink-0" />
					<span className="truncate">{record.addressLine || 'No address'}</span>
				</button>
			</div>

			{metres !== null && (
				<p className="shrink-0 text-right text-xs tabular-nums text-gray-600">
					{formatDistance(metres)}
					{bearing && <span className="ml-1 text-gray-400">{bearing}</span>}
				</p>
			)}

			{placeable ? (
				<button
					type="button"
					onClick={() => onDirections(record)}
					aria-label={`Directions to ${record.name}`}
					className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-primary"
				>
					<Navigation className="h-4 w-4" />
				</button>
			) : (
				// Same width, so the rows above and below do not shift.
				<span
					className="inline-flex h-11 w-11 shrink-0 items-center justify-center text-gray-300"
					title="No coordinates for this record"
				>
					<Compass className="h-4 w-4" />
				</span>
			)}
		</li>
	)
}

export default NearMe
